/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { isCluster2x } from '@shared/domain/cluster';
import { ClusterInfo, ClusterStatus } from '@shared/types/cluster';
import { TDEType } from '@shared/types/nebula';
import { P, match } from 'ts-pattern';

export const findClusterById = (clusters: ClusterInfo[], clusterId: string) => clusters.find((c) => c.id === clusterId);

export const isNebulaCluster = (cluster: ClusterInfo): boolean => !!cluster.nebula;

export const isNebulaInStatus = (cluster: ClusterInfo, statuses: ClusterStatus[]) => {
  return cluster?.status ? statuses.includes(cluster.status) : false;
};

export const isClusterOnline = (clusterInfo: ClusterInfo) => clusterInfo.connected;

export const isClusterActive = (cluster: ClusterInfo) =>
  !isNebulaInStatus(cluster, [ClusterStatus.FAILED, ClusterStatus.DESTROYED, ClusterStatus.DESTROYING]);

export const isPrivateLinkNebulaCluster = (cluster: ClusterInfo): boolean => !!cluster?.nebula?.privateLink;

export const isAWSDataEncryptedCluster = (cluster: ClusterInfo): boolean =>
  cluster?.encryption?.tdeType === TDEType.AWS_KMS;

export const isTaskPrioritizationEnabled = (cluster: ClusterInfo) =>
  isCluster2x(cluster) && !!cluster.info.configuration.priorityQueueSpiEnabled;

export const getClusterStatus = (cluster?: ClusterInfo) => {
  return cluster?.status ? cluster.status : null;
};

export const getClusterStatusText = (status?: ClusterStatus | null) =>
  match(status)
    .with(ClusterStatus.PROVISIONING, () => `Provisioning`)
    .with(ClusterStatus.DESTROYING, () => `Destroying`)
    .with(ClusterStatus.FAILED, () => `Failed`)
    .with(ClusterStatus.DESTROYED, () => 'Destroyed')
    .with(ClusterStatus.ACTIVE, () => `Active`)
    .with(ClusterStatus.INACTIVE, () => `Inactive`)
    .with(ClusterStatus.DISCONNECTED, () => `Disconnected`)
    .with(ClusterStatus.SUSPENDING, () => 'Suspending')
    .with(ClusterStatus.SUSPENDED, () => 'Suspended')
    .with(ClusterStatus.RESUMING, () => 'Resuming')
    .with(ClusterStatus.LIMITED, () => 'Limited')
    .with(ClusterStatus.UNINITIALIZED, () => 'Uninitialized')
    .with(P.nullish, () => null)
    .exhaustive();

export const getClusterStatusTooltip = (
  status?: ClusterStatus | null,
  isNebulaCluster: boolean = false,
): string | null => {
  switch (status) {
    case ClusterStatus.LIMITED:
      return `Monitoring is limited due to excessive load. Some data is not shown. Consider collecting less metrics, traces, and compute info.`;
    case ClusterStatus.FAILED:
      return isNebulaCluster
        ? 'Failed to start. Contact the support team.'
        : getClusterStatusText(ClusterStatus.FAILED);
    default:
      return getClusterStatusText(status);
  }
};
